import {useCallback, useRef, useState} from 'react';

import {uniqBy} from 'lodash';
import {v4 as uuidv4} from 'uuid';
import {
  FCFileTransferInternalState,
  FCFileTransferState,
  getFileNameFromRemotePath,
  getStoragePathParam,
  MAX_UPLOAD_QUAUE_SIZE,
  showFCToastFromAxiosError,
} from '../../utils/fcUploadFileUtils';

import {AxiosError} from 'axios';
import {t} from 'i18next';
import {useSelector} from 'react-redux';
import {
  deleteUploadedFile,
  initiateIntakeUpload,
} from '../../api/EvidenceSourceUpload';
import {showCustomMessageToast, showErrorToast} from '../../api/utils';
import {showToastType} from '../../contexts/toastContext/ToastContext';
import {RootState} from '../../redux/store';
import {
  InitiateUploadPayload,
  UploadMethod,
} from '../../types/upload-new-eviidence/evidenceSourceTypes';
import {
  FCFileGridItem,
  FCLocalDirectory,
} from '../../types/upload-new-eviidence/fcUploadFileTypes';
import {isEmptyInstance} from '../../utils/utils';
import FCUploadFileTask from './FCUploadFileTask';
import useFCAgentConnection from './useFCAgentConnection';
import useFCStatusPolling from './useFCStatusPolling';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../permissions/usePermissionsByPage';
import {
  GA_ERROR_CODES,
  GA_ERROR_TYPES,
} from '../../constants/analyticsConstants';

const useFCUploadFiles = (
  fcEncryptedPayload: string,
  connectionKey: string,
  showToast: showToastType
) => {
  const {regenerateConnectionKey} = useFCAgentConnection(false);

  const {
    startPolling: startStatusPolling,
    stopPolling: stopStatusPolling,
    setCallBack,
  } = useFCStatusPolling();

  const [pendingFileArray, setPendingFileArray] = useState<FCFileGridItem[]>(
    []
  );
  const [uploadedFileArray, setUploadedFileArray] = useState<FCFileGridItem[]>(
    []
  );

  const [isIntakeLoading, setIsIntakeLoading] = useState<boolean>(false);

  const pendingFileArrayMap = useRef<Map<string, FCFileGridItem[]>>(new Map());
  const uploadedFileArrayMap = useRef<Map<string, FCFileGridItem[]>>(new Map());
  const uploadTaskMap = useRef<Map<string, FCUploadFileTask>>(new Map());
  const deletedUploadTaskMap = useRef<Map<string, FCUploadFileTask>>(new Map());

  const inProgressUploadTaskIdSet = useRef<Set<string>>(new Set());
  const isAllJobStatusCheckDone = useRef<boolean>(true);

  const clientId = useSelector(
    (state: RootState) => state.selectedClient.clientId
  );

  const projectId = useSelector(
    (state: RootState) =>
      state.uploadEvidenceSource.selectedProject?.projectId ?? ''
  );

  const {sourceDetails, evidenceSourceSessionId = ''} = useSelector(
    (state: RootState) => state.uploadEvidenceSource
  );

  const projectAlphaCode = useSelector(
    (state: RootState) =>
      state.uploadEvidenceSource.selectedProject?.projectAlphaCode ?? ''
  );

  const {canManageEvidence} = usePermissionsByPage(
    PAGE_NAME.UPLOAD_EVIDENCE_SOURCE,
    projectId
  );

  const isAllTransferCompleted = useCallback(
    () =>
      ![...uploadTaskMap.current.values()].some(
        (uploadTask: FCUploadFileTask) =>
          !(
            (uploadTask?.fileTransferSession?.transferComplete ?? false) ||
            (uploadTask?.fileTransferSession?.transferCancelled ?? false) ||
            (uploadTask?.isUploadTaskCompleted ?? false) ||
            (uploadTask?.isUploadTaskCancelled ?? false) ||
            [
              FCFileTransferState.COMPLETED,
              FCFileTransferState.FAILED,
            ].includes(uploadTask?.fileTransferState)
          )
      ),
    []
  );

  const isUploadExistForFile = useCallback((file: FCLocalDirectory) => {
    return [...uploadTaskMap.current.values()].some(
      (uploadTask: FCUploadFileTask) =>
        !(
          (uploadTask?.fileTransferSession?.transferComplete ?? false) ||
          (uploadTask?.fileTransferSession?.transferCancelled ?? false)
        ) &&
        (uploadTask?.fileTransferSession?.localFile?.path == file?.path ||
          uploadTask?.fileTransferSession?.localFile?.name == file?.name)
    );
  }, []);

  const removeFileFromUploadedArray = useCallback((remoteFilename: string) => {
    for (const [, uploadTask] of uploadTaskMap.current) {
      uploadTask?.removeUploadedFileFromArray(remoteFilename);
    }
    for (const [, uploadTask] of deletedUploadTaskMap.current) {
      uploadTask?.removeUploadedFileFromArray(remoteFilename);
    }
  }, []);

  const getJobIdStatus = useCallback(async () => {
    if (!isAllJobStatusCheckDone.current) {
      return;
    }

    isAllJobStatusCheckDone.current = false;

    const iterator = uploadTaskMap.current.entries();
    let result = iterator.next();

    while (!result.done) {
      const [uploadTaskId, uploadTask] = result.value;

      if (
        inProgressUploadTaskIdSet.current.has(uploadTaskId) &&
        uploadTask?.fileTransferState !== FCFileTransferState.RECONNECTING
      ) {
        switch (uploadTask?.fileTransferInternalState) {
          case FCFileTransferInternalState.INITTASK: {
            await uploadTask?.connectWithFC();
            break;
          }
          case FCFileTransferInternalState.CONNECTED: {
            await uploadTask?.getJobIdStatus();
            break;
          }
        }
      }

      if (isAllTransferCompleted()) {
        stopStatusPolling();
        inProgressUploadTaskIdSet.current.clear();
        isAllJobStatusCheckDone.current = true;
        break;
      }

      result = iterator.next();
      if (result.done) {
        isAllJobStatusCheckDone.current = true;
      }
    }
  }, [isAllTransferCompleted, stopStatusPolling]);

  const updatePendingFileArray = useCallback(
    (taskId: string, updatedPendingFileArray: FCFileGridItem[]) => {
      pendingFileArrayMap.current.set(taskId, updatedPendingFileArray.slice());

      const latestPendingFileArray: FCFileGridItem[] = uniqBy(
        Array.from(pendingFileArrayMap.current.values()).flat(),
        'id'
      ).slice();

      setPendingFileArray(latestPendingFileArray);
    },
    []
  );

  const updateUploadedFileArray = useCallback(
    (taskId: string, updatedUploadedFileArray: FCFileGridItem[]) => {
      uploadedFileArrayMap.current.set(
        taskId,
        updatedUploadedFileArray.slice()
      );

      const latestUploadedFileArray: FCFileGridItem[] = uniqBy(
        uniqBy([...uploadedFileArrayMap.current.values()].flat(), 'id'),
        'remoteFilename'
      ).slice();

      setUploadedFileArray(latestUploadedFileArray);
    },
    []
  );

  const getNextUploadTaskIds = useCallback((fillSize: number) => {
    const taskIds = [];

    for (const [uploadTaskId, uploadTask] of uploadTaskMap.current) {
      if (
        uploadTask?.fileTransferInternalState ===
          FCFileTransferInternalState.INITTASK &&
        uploadTask?.fileTransferState !== FCFileTransferState.FAILED &&
        !inProgressUploadTaskIdSet.current.has(uploadTaskId)
      ) {
        taskIds.push(uploadTaskId);
      }

      if (taskIds.length === fillSize) {
        break;
      }
    }

    return taskIds;
  }, []);

  const cancelAllPendingUpload = useCallback(async () => {
    const currentPendingFileArray: FCFileGridItem[] = uniqBy(
      Array.from(pendingFileArrayMap.current.values()).flat(),
      'id'
    ).slice();

    await Promise.allSettled(
      currentPendingFileArray
        .filter((fileGridItem: FCFileGridItem) => {
          const uploadTaskId = fileGridItem?.uploadTaskId ?? '';
          const uploadTask: FCUploadFileTask | undefined =
            uploadTaskMap.current?.get(uploadTaskId);
          return (
            !isEmptyInstance(uploadTask) &&
            uploadTask?.fileTransferState !== FCFileTransferState.FAILED
          );
        })
        .map(async (fileGridItem: FCFileGridItem) => {
          const uploadTaskId = fileGridItem?.uploadTaskId ?? '';
          const uploadTask: FCUploadFileTask | undefined =
            uploadTaskMap.current?.get(uploadTaskId);
          await uploadTask?.cancelFileTransfer(fileGridItem);
        })
    );
  }, []);

  const onTransferCompleted = useCallback(
    (uploadTaskId: string) => {
      inProgressUploadTaskIdSet.current.delete(uploadTaskId);

      const uploadTask: FCUploadFileTask | undefined =
        uploadTaskMap.current?.get(uploadTaskId);

      if (!isEmptyInstance(uploadTask)) {
        switch (uploadTask?.fileTransferState) {
          case FCFileTransferState.CANCELLED: {
            const uploadTaskCopy: FCUploadFileTask = Object.assign(
              Object.create(Object.getPrototypeOf(uploadTask)),
              uploadTask
            );
            deletedUploadTaskMap.current.set(uploadTaskId, uploadTaskCopy);

            uploadTaskMap.current.delete(uploadTaskId);
            pendingFileArrayMap.current.delete(uploadTaskId);
            uploadedFileArrayMap.current.delete(uploadTaskId);
            break;
          }

          case FCFileTransferState.FAILED: {
            showCustomMessageToast(
              showToast,
              t('fileCatalyst.failedToUpload'),
              'error',
              {
                code: GA_ERROR_CODES.INTERNAL_SERVER_ERR_500,
                type: GA_ERROR_TYPES.FILE_UPLOAD_ERROR,
              }
            );
            break;
          }
        }
      }

      if (inProgressUploadTaskIdSet.current.size < MAX_UPLOAD_QUAUE_SIZE) {
        const nextUploadTaskIds: string[] = getNextUploadTaskIds(
          MAX_UPLOAD_QUAUE_SIZE - inProgressUploadTaskIdSet.current.size
        );

        nextUploadTaskIds.forEach((taskId: string) =>
          inProgressUploadTaskIdSet.current.add(taskId)
        );
      }
    },
    [getNextUploadTaskIds, showToast]
  );

  const onFCError = useCallback(
    (error: AxiosError) => {
      showFCToastFromAxiosError(showToast, error, t('common.somethingWrong'));
    },
    [showToast]
  );

  const handleUploadLocalFile = useCallback(
    (file: FCLocalDirectory) => {
      if (isUploadExistForFile(file)) {
        showCustomMessageToast(
          showToast,
          t('fileCatalyst.fileUploadInProgress', {fileName: file.name}),
          'warning'
        );
      } else {
        const uploadTaskId = `${uuidv4()}`;

        pendingFileArrayMap.current.set(uploadTaskId, []);
        uploadedFileArrayMap.current.set(uploadTaskId, []);

        const uploadTask: FCUploadFileTask = new FCUploadFileTask({
          uploadTaskId,
          fcEncryptedPayload,
          projectAlphaCode: projectAlphaCode ?? '',
          clientId: clientId ?? '',
          projectId: projectId ?? '',
          evidenceSourceSessionId: evidenceSourceSessionId ?? '',
          localFile: file,
          onUpdatePendingFileArray: updatePendingFileArray,
          onUpdateUploadedFileArray: updateUploadedFileArray,
          onTransferCompleted,
          onTransferFailed: onTransferCompleted,
          onFCError: onFCError,
        });

        uploadTaskMap.current.set(uploadTaskId, uploadTask);

        if (inProgressUploadTaskIdSet.current.size < MAX_UPLOAD_QUAUE_SIZE) {
          inProgressUploadTaskIdSet.current.add(uploadTaskId);
        }

        setCallBack(getJobIdStatus);
        startStatusPolling();
      }
    },
    [
      clientId,
      evidenceSourceSessionId,
      fcEncryptedPayload,
      getJobIdStatus,
      isUploadExistForFile,
      onFCError,
      onTransferCompleted,
      projectAlphaCode,
      projectId,
      setCallBack,
      showToast,
      startStatusPolling,
      updatePendingFileArray,
      updateUploadedFileArray,
    ]
  );

  const deleteFileFromEvidenceSourceUpload = useCallback(
    async (uploadSessionId?: string) => {
      try {
        if (
          !isEmptyInstance(uploadSessionId) &&
          !isEmptyInstance(clientId) &&
          !isEmptyInstance(projectId)
        ) {
          await deleteUploadedFile(clientId, projectId, uploadSessionId);
        }
      } catch (error) {
        const err = error as AxiosError;
        showErrorToast(showToast, err, 'center');
      }
    },
    [clientId, projectId, showToast]
  );

  const onSuccessDeleteRemoteFile = useCallback(
    (fileGridItem: FCFileGridItem) => {
      const uploadTaskId = fileGridItem?.uploadTaskId ?? '';
      let uploadTask: FCUploadFileTask | undefined =
        uploadTaskMap.current?.get(uploadTaskId);

      if (isEmptyInstance(uploadTask)) {
        uploadTask = deletedUploadTaskMap.current?.get(uploadTaskId);
      }

      if (!isEmptyInstance(uploadTask) && !isEmptyInstance(uploadTaskId)) {
        deleteFileFromEvidenceSourceUpload(fileGridItem?.uploadSessionId);
        removeFileFromUploadedArray(fileGridItem?.remoteFilename ?? '');
        if (uploadTask?.fileTransferSession?.localFile?.isFile ?? false) {
          uploadTaskMap.current.delete(uploadTaskId);
          pendingFileArrayMap.current.delete(uploadTaskId);
          uploadedFileArrayMap.current.delete(uploadTaskId);
        }
      }
    },
    [deleteFileFromEvidenceSourceUpload, removeFileFromUploadedArray]
  );

  const handleDeleteRemoteFile = useCallback(
    (fileGridItem: FCFileGridItem) => {
      const uploadTaskId = fileGridItem?.uploadTaskId ?? '';
      let uploadTask: FCUploadFileTask | undefined =
        uploadTaskMap.current?.get(uploadTaskId);

      if (isEmptyInstance(uploadTask)) {
        uploadTask = deletedUploadTaskMap.current?.get(uploadTaskId);
      }

      if (!isEmptyInstance(uploadTask)) {
        uploadTask?.deleteRemoteFile(
          connectionKey,
          fileGridItem,
          (success: boolean) => {
            if (success) {
              onSuccessDeleteRemoteFile(fileGridItem);
            } else {
              regenerateConnectionKey(fcEncryptedPayload).then(
                (updatedConnectionKey: string | null) => {
                  if (!isEmptyInstance(updatedConnectionKey)) {
                    uploadTask?.deleteRemoteFile(
                      updatedConnectionKey,
                      fileGridItem,
                      (success: boolean) => {
                        if (success) {
                          onSuccessDeleteRemoteFile(fileGridItem);
                        }
                      }
                    );
                  }
                }
              );
            }
          }
        );
      }
    },
    [
      connectionKey,
      fcEncryptedPayload,
      onSuccessDeleteRemoteFile,
      regenerateConnectionKey,
    ]
  );

  const handleCancelUploadFileAction = useCallback(
    async (fileGridItem: FCFileGridItem) => {
      const uploadTaskId = fileGridItem?.uploadTaskId ?? '';
      const uploadTask: FCUploadFileTask | undefined =
        uploadTaskMap.current?.get(uploadTaskId);

      if (
        !isEmptyInstance(uploadTask) &&
        !uploadTask?.pendingFileGridItemArray.some(
          (pendingFile: FCFileGridItem) => pendingFile?.id === fileGridItem?.id
        )
      ) {
        showCustomMessageToast(
          showToast,
          t('fileCatalyst.uploadCannotBeCancelled')
        );
      } else if (
        !isEmptyInstance(uploadTask) &&
        uploadTask?.fileTransferState !== FCFileTransferState.FAILED
      ) {
        const updatedFileGridItem = uploadTask?.pendingFileGridItemArray.find(
          (pendingFile: FCFileGridItem) => pendingFile?.id === fileGridItem?.id
        );
        !isEmptyInstance(updatedFileGridItem) &&
          (await uploadTask.cancelFileTransfer(updatedFileGridItem));
      }
    },
    [showToast]
  );

  const handleRetryFailedUploadFileAction = useCallback(
    async (fileGridItem: FCFileGridItem) => {
      const uploadTaskId = fileGridItem?.uploadTaskId ?? '';
      const uploadTask: FCUploadFileTask | undefined =
        uploadTaskMap.current?.get(uploadTaskId);

      if (!isEmptyInstance(uploadTask)) {
        const localFile = uploadTask?.fileTransferSession?.localFile ?? null;

        if (
          uploadTask?.fileTransferState === FCFileTransferState.FAILED &&
          !isEmptyInstance(localFile)
        ) {
          const latestPendingFileArray: FCFileGridItem[] = uniqBy(
            Array.from(pendingFileArrayMap.current.values()).flat(),
            'id'
          ).slice();

          setPendingFileArray(latestPendingFileArray);

          const updatedUploadTask: FCUploadFileTask = new FCUploadFileTask({
            uploadTaskId,
            fcEncryptedPayload,
            projectAlphaCode: projectAlphaCode ?? '',
            clientId: clientId ?? '',
            projectId: projectId ?? '',
            evidenceSourceSessionId: evidenceSourceSessionId ?? '',
            localFile,
            onUpdatePendingFileArray: updatePendingFileArray,
            onUpdateUploadedFileArray: updateUploadedFileArray,
            onTransferCompleted,
            onTransferFailed: onTransferCompleted,
            onFCError: onFCError,
          });
          updatedUploadTask.uploadTaskId = uploadTaskId;
          updatedUploadTask.uploadedFileGridItemArray = [
            ...(uploadTask?.uploadedFileGridItemArray ?? []),
          ];

          uploadTaskMap.current.set(uploadTaskId, updatedUploadTask);

          if (inProgressUploadTaskIdSet.current.size < MAX_UPLOAD_QUAUE_SIZE) {
            inProgressUploadTaskIdSet.current.add(uploadTaskId);
          }

          setCallBack(getJobIdStatus);
          startStatusPolling();
        }
      }
    },
    [
      clientId,
      evidenceSourceSessionId,
      fcEncryptedPayload,
      getJobIdStatus,
      onFCError,
      onTransferCompleted,
      projectAlphaCode,
      projectId,
      setCallBack,
      startStatusPolling,
      updatePendingFileArray,
      updateUploadedFileArray,
    ]
  );

  const handleInitiateUpload = useCallback(async () => {
    setIsIntakeLoading(true);

    const payload: InitiateUploadPayload = {
      name: sourceDetails?.evidenceName,
      clientReference: sourceDetails?.clientReference,
      hasManifest: !!sourceDetails?.uploadedManifest,
      hasPasswords: Boolean(sourceDetails?.password?.length),
      hasPasskey: (sourceDetails?.uploadedPasskey?.length ?? 0) > 0,
      hasSpecialHandling: !!sourceDetails?.handlingInstructions,
      passwords: sourceDetails?.password,
      custodianType: sourceDetails?.custodianType,
      custodianId: String(sourceDetails?.singleCustodian?.id ?? ''),
      custodianName: sourceDetails?.singleCustodian?.displayName ?? '',
      custodianFirstName: sourceDetails?.singleCustodian?.firstName ?? '',
      custodianLastName: sourceDetails?.singleCustodian?.lastName ?? '',
      processingTrigger: sourceDetails?.intakeCompletion.value,
      specialHandlingInstructions: sourceDetails?.handlingInstructions,
      additionalInstructions: sourceDetails?.isAdditionalInstructions,
      manifests: sourceDetails?.uploadedManifest
        ? {
            fileId: sourceDetails.uploadedManifest.fileId ?? '',
            fileSizeInBytes:
              sourceDetails.uploadedManifest.fileSizeInBytes ?? 0,
            fileName: sourceDetails.uploadedManifest.fileName ?? '',
            storagePath: sourceDetails.uploadedManifest.path ?? '',
          }
        : undefined,
      passkeys: sourceDetails?.uploadedPasskey
        ? sourceDetails.uploadedPasskey.map((uploadedPasskey) => {
            return {
              fileId: uploadedPasskey.fileId ?? '',
              fileSizeInBytes: uploadedPasskey.fileSizeInBytes ?? 0,
              fileName: uploadedPasskey.fileName ?? '',
              storagePath: uploadedPasskey.path ?? '',
            };
          })
        : undefined,
      uploadMethod: UploadMethod.HighSpeed,
      uploads: uploadedFileArray
        .filter((item: FCFileGridItem) => (item?.size ?? 0) > 0)
        .map((item: FCFileGridItem) => ({
          fileId: item?.uploadSessionId || '',
          fileSizeInBytes: item?.size ?? 0,
          fileName: getFileNameFromRemotePath(
            item?.remoteDirectory ?? '',
            item?.name ?? ''
          ),
          storagePath: getStoragePathParam(item?.remoteDirectory ?? ''),
        })),
      auroraType:
        sourceDetails?.defaultProcessingSettings?.find(
          (item: any) => item.id === 'serviceType'
        )?.value ?? '',
    };

    try {
      const response = await initiateIntakeUpload(
        clientId,
        projectId,
        evidenceSourceSessionId ?? '',
        payload
      );
      showToast(
        t('fileCatalyst.intakeSuccess'),
        'success',
        undefined,
        'center'
      );
      return response.data;
    } catch (error) {
      showErrorToast(showToast, error as AxiosError, 'center');
      return null;
    } finally {
      setIsIntakeLoading(false);
    }
  }, [
    clientId,
    projectId,
    showToast,
    sourceDetails,
    uploadedFileArray,
    evidenceSourceSessionId,
  ]);

  return {
    pendingFileArray,
    uploadedFileArray,
    canManageEvidence,
    handleDeleteRemoteFile,
    handleCancelUploadFileAction,
    handleRetryFailedUploadFileAction,
    handleUploadLocalFile,
    isIntakeLoading,
    handleInitiateUpload,
    stopStatusPolling,
    cancelAllPendingUpload,
  };
};

export default useFCUploadFiles;
